import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useCart, keyOf } from "@/lib/cart";
import { useCustomerAuth } from "@/lib/customerAuth";
import { api, formatPKR, LOGO_URL } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Lock,
  ChevronUp,
  ChevronDown,
  ShoppingBag,
  Banknote,
  Truck,
  Check,
  ArrowLeft,
  Tag,
} from "lucide-react";

const SHIPPING_FEE = 380;

function resolveImage(url) {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

function configLine(c) {
  if (!c) return "";
  const parts = [];
  if (c.bedding_type) parts.push(c.bedding_type);
  if (c.size) parts.push(c.size);
  if (c.pc_count) parts.push(`${c.pc_count} pc Set`);
  if (c.pillow_count) parts.push(`${c.pillow_count} Pillows`);
  if (c.curtain_size) parts.push(c.curtain_size);
  return parts.join(" · ");
}

const PROVINCES = ["Punjab", "Sindh", "Khyber Pakhtunkhwa", "Balochistan", "Islamabad Capital Territory", "Gilgit-Baltistan", "Azad Jammu & Kashmir"];

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, count, discount, discountQualifiesAny, clear } = useCart();
  const { customer } = useCustomerAuth();
  const [submitting, setSubmitting] = useState(false);
  const [showSummaryMobile, setShowSummaryMobile] = useState(false);
  const [discountCode, setDiscountCode] = useState("");

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    province: "Punjab",
    postal_code: "",
    notes: "",
  });

  useEffect(() => {
    if (customer) {
      setForm((f) => ({
        ...f,
        full_name: f.full_name || customer.full_name || "",
        email: f.email || customer.email || "",
        phone: f.phone || customer.phone || "",
      }));
    }
  }, [customer]);

  const shippingFee = SHIPPING_FEE;
  const total = subtotal - discount + (items.length ? shippingFee : 0);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Your bag is empty");
      return;
    }
    if (!form.full_name || !form.email || !form.phone || !form.address_line1 || !form.city) {
      toast.error("Please complete all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer: form,
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          image: i.image || null,
          category: i.category,
          config: i.config || null,
        })),
        subtotal,
        discount,
        shipping: shippingFee,
        total,
        payment_method: "COD",
      };
      const { data } = await api.post("/orders", payload);
      clear();
      toast.success("Order placed");
      navigate(`/order/${data.id}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Could not place order");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <EmptyCheckout />
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="checkout-page">
      {/* Slim Header */}
      <header className="border-b border-border-subtle bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="checkout-logo">
            <img src={LOGO_URL} alt="" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="font-display text-xl text-ink">Bedding Lounge</div>
              <div className="label-eyebrow !text-[9px]">Checkout</div>
            </div>
          </Link>
          <Link to="/" className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink-soft hover:text-sage" data-testid="checkout-continue-shopping">
            <ArrowLeft size={14} /> Continue Shopping
          </Link>
        </div>
      </header>

      {/* Mobile order summary toggle */}
      <div className="lg:hidden border-b border-border-subtle bg-cream-warm/60">
        <button
          onClick={() => setShowSummaryMobile((v) => !v)}
          className="w-full max-w-6xl mx-auto px-5 py-3 min-h-[56px] flex items-center justify-between"
          data-testid="mobile-summary-toggle"
        >
          <span className="flex items-center gap-2 text-sm text-sage font-semibold">
            <ShoppingBag size={16} />
            {showSummaryMobile ? "Hide" : "Show"} order summary
            {showSummaryMobile ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </span>
          <span className="font-display text-xl text-ink">{formatPKR(total)}</span>
        </button>
        <AnimatePresence>
          {showSummaryMobile && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden border-t border-border-subtle"
            >
              <div className="max-w-6xl mx-auto px-4 py-4">
                <SummaryContent
                  items={items}
                  subtotal={subtotal}
                  discount={discount}
                  discountQualifiesAny={discountQualifiesAny}
                  shippingFee={shippingFee}
                  total={total}
                  discountCode={discountCode}
                  setDiscountCode={setDiscountCode}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-12 py-7 lg:py-12 pb-28 lg:pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          {/* LEFT — Form */}
          <form id="checkout-form" onSubmit={submit} className="lg:col-span-7" data-testid="checkout-form">
            {/* Step indicator */}
            <ol className="flex items-center gap-2 text-xs uppercase tracking-widest text-ink-soft mb-8">
              <li className="text-sage font-semibold">Cart</li>
              <span>›</span>
              <li className="text-ink font-semibold">Information</li>
              <span>›</span>
              <li>Confirmation</li>
            </ol>

            {/* Contact */}
            <SectionHeading number="01" title="Contact" />
            {customer ? (
              <div className="bg-cream-warm border border-border-subtle px-4 py-3 mb-2 text-sm flex items-center justify-between" data-testid="checkout-customer-banner">
                <span className="text-ink">{customer.email}</span>
                <span className="label-eyebrow !text-[10px] text-sage">Signed in</span>
              </div>
            ) : (
              <div className="text-xs text-ink-soft mb-2">
                Have an account?{" "}
                <Link to="/login?next=/checkout" className="text-sage hover:text-sage-dark font-semibold" data-testid="checkout-signin-link">
                  Sign in
                </Link>
              </div>
            )}
            <div className="space-y-4">
              <Field label="Email" required>
                <Input type="email" inputMode="email" autoComplete="email" value={form.email} onChange={update("email")} required placeholder="you@email.com" data-testid="input-email" className="bg-white border-border-subtle h-12" />
              </Field>
              <Field label="Phone" required help="We may text you to confirm delivery.">
                <Input type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={update("phone")} required placeholder="03XX-XXXXXXX" data-testid="input-phone" className="bg-white border-border-subtle h-12" />
              </Field>
            </div>

            {/* Delivery */}
            <div className="mt-10">
              <SectionHeading number="02" title="Delivery Address" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full Name" required wide>
                  <Input autoComplete="name" value={form.full_name} onChange={update("full_name")} required data-testid="input-full-name" className="bg-white border-border-subtle h-12" />
                </Field>
                <Field label="Address" required wide>
                  <Input autoComplete="address-line1" value={form.address_line1} onChange={update("address_line1")} required placeholder="House #, Street" data-testid="input-address1" className="bg-white border-border-subtle h-12" />
                </Field>
                <Field label="Apartment / Suite (optional)" wide>
                  <Input autoComplete="address-line2" value={form.address_line2} onChange={update("address_line2")} placeholder="Apt, Block, Sector" data-testid="input-address2" className="bg-white border-border-subtle h-12" />
                </Field>
                <Field label="City" required>
                  <Input autoComplete="address-level2" value={form.city} onChange={update("city")} required placeholder="Lahore" data-testid="input-city" className="bg-white border-border-subtle h-12" />
                </Field>
                <Field label="Province" required>
                  <select
                    value={form.province}
                    onChange={update("province")}
                    autoComplete="address-level1"
                    className="mt-1.5 w-full h-12 px-3 bg-white border border-border-subtle text-ink text-sm focus:outline-none focus:ring-2 focus:ring-sage focus:border-sage rounded-md"
                    data-testid="select-province"
                  >
                    {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Postal Code">
                  <Input inputMode="numeric" autoComplete="postal-code" value={form.postal_code} onChange={update("postal_code")} placeholder="54000" data-testid="input-postal" className="bg-white border-border-subtle h-12" />
                </Field>
                <Field label="Order Notes (optional)" wide>
                  <Textarea value={form.notes} onChange={update("notes")} placeholder="Special delivery instructions" data-testid="input-notes" className="bg-white border-border-subtle min-h-20" />
                </Field>
              </div>
            </div>

            {/* Shipping method */}
            <div className="mt-10">
              <SectionHeading number="03" title="Shipping Method" />
              <div className="border border-border-subtle divide-y divide-border-subtle" data-testid="shipping-method">
                <ShippingOption
                  selected
                  label="Standard Shipping"
                  desc="Pakistan-wide · 3–7 business days"
                  price={formatPKR(shippingFee)}
                />
              </div>
            </div>

            {/* Payment */}
            <div className="mt-10">
              <SectionHeading number="04" title="Payment" />
              <p className="text-xs text-ink-soft mb-3">All transactions are secure. No card needed.</p>
              <div className="border border-sage bg-sage/5 px-4 py-4 flex items-start gap-3" data-testid="payment-cod">
                <div className="mt-0.5 w-5 h-5 rounded-full border-2 border-sage flex items-center justify-center flex-shrink-0">
                  <div className="w-2.5 h-2.5 rounded-full bg-sage" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Banknote size={16} className="text-sage" />
                    <span className="font-semibold text-ink">Cash on Delivery (COD)</span>
                  </div>
                  <div className="text-sm text-ink-soft mt-1">Pay in cash when your order arrives. We'll call to confirm.</div>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <Link to="/" className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink-soft hover:text-sage" data-testid="back-to-cart-link">
                <ArrowLeft size={14} /> Return to cart
              </Link>
              <Button
                type="submit"
                disabled={submitting}
                className="hidden sm:inline-flex w-full sm:w-auto bg-sage hover:bg-sage-dark text-cream px-12 py-6 text-xs uppercase tracking-[0.22em] font-semibold transition-colors disabled:opacity-50"
                data-testid="checkout-submit"
              >
                <Lock size={14} className="mr-2" />
                {submitting ? "Placing order…" : "Place Order"}
              </Button>
            </div>

            {/* Trust */}
            <div className="mt-10 pt-8 border-t border-border-subtle text-xs text-ink-soft text-center">
              <Lock size={11} className="inline mr-1.5 -mt-0.5" />
              All your information is private and used only to fulfil this order.
            </div>
          </form>

          {/* RIGHT — Sticky summary */}
          <aside className="hidden lg:block lg:col-span-5">
            <div className="sticky top-8 bg-cream-warm/40 border border-border-subtle p-6">
              <SummaryContent
                items={items}
                subtotal={subtotal}
                discount={discount}
                discountQualifiesAny={discountQualifiesAny}
                shippingFee={shippingFee}
                total={total}
                discountCode={discountCode}
                setDiscountCode={setDiscountCode}
              />
            </div>
          </aside>
        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-border-subtle text-xs text-ink-soft flex flex-col sm:flex-row sm:justify-between items-center gap-3" data-testid="checkout-footer">
          <div>© {new Date().getFullYear()} Bedding Lounge</div>
          <div className="flex items-center gap-4 uppercase tracking-widest">
            <Link to="/" className="hover:text-sage">Home</Link>
            <Link to="/category/bedding" className="hover:text-sage">Shop</Link>
            <a href="mailto:Beddinglounge@gmail.com" className="hover:text-sage">Contact</a>
          </div>
        </footer>
      </div>

      {/* Mobile sticky submit — total + Place Order always within reach */}
      <div
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-lg border-t border-border-subtle px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-4px_24px_-12px_rgba(43,45,42,0.22)]"
        data-testid="checkout-mobile-bar"
      >
        <div className="flex items-center gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft font-semibold">Total · PKR</div>
            <div className="font-display text-2xl text-ink leading-none mt-0.5" data-testid="mobile-bar-total">{formatPKR(total)}</div>
          </div>
          <Button
            type="submit"
            form="checkout-form"
            disabled={submitting}
            className="flex-1 bg-sage hover:bg-sage-dark text-cream py-6 text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors disabled:opacity-50"
            data-testid="checkout-submit-mobile"
          >
            <Lock size={13} className="mr-2" />
            {submitting ? "Placing…" : "Place Order"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SectionHeading({ number, title }) {
  return (
    <div className="flex items-baseline gap-3 mb-4">
      <span className="label-eyebrow !text-[10px] !text-sage">{number}</span>
      <h2 className="font-display text-2xl text-ink">{title}</h2>
    </div>
  );
}

function Field({ label, required, help, wide, children }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <Label className="text-ink-soft text-[11px] uppercase tracking-widest">
        {label} {required && <span className="text-rose-dark">*</span>}
      </Label>
      {children}
      {help && <p className="text-[11px] text-ink-soft mt-1">{help}</p>}
    </div>
  );
}

function ShippingOption({ selected, label, desc, price }) {
  return (
    <div className={`px-4 py-4 flex items-center gap-3 ${selected ? "bg-sage/5" : ""}`}>
      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? "border-sage" : "border-border-subtle"}`}>
        {selected && <div className="w-2.5 h-2.5 rounded-full bg-sage" />}
      </div>
      <div className="flex-1 flex items-center justify-between">
        <div>
          <div className="text-ink font-semibold text-sm">{label}</div>
          <div className="text-xs text-ink-soft">{desc}</div>
        </div>
        <div className="text-ink font-semibold text-sm">{price}</div>
      </div>
    </div>
  );
}

function SummaryContent({ items, subtotal, discount, discountQualifiesAny, shippingFee, total, discountCode, setDiscountCode }) {
  return (
    <>
      {discountQualifiesAny && (
        <div className="mb-4 bg-sage/10 border border-sage/40 px-3 py-2 text-xs text-ink flex items-center gap-2" data-testid="checkout-discount-banner">
          <Tag size={13} className="text-sage" />
          <span><span className="font-semibold text-sage-dark">5% bulk discount applied</span> — on Bedsheet/Fitted Sheet items (3+ in cart).</span>
        </div>
      )}
      <ul className="space-y-4">
        {items.map((it) => (
          <li key={keyOf(it)} className="flex gap-3 items-start" data-testid={`summary-item-${it.product_id}`}>
            <div className="relative w-16 h-16 bg-cream-warm flex-shrink-0 overflow-hidden border border-border-subtle">
              {it.image ? (
                <img src={resolveImage(it.image)} alt={it.name} className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <div className="h-full flex items-center justify-center font-display text-[10px] text-sage">BL</div>
              )}
              <span className="absolute -top-2 -right-2 bg-ink text-cream text-[10px] font-semibold rounded-full h-5 min-w-5 px-1 flex items-center justify-center">
                {it.quantity}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-ink text-sm leading-tight truncate">{it.name}</div>
              {configLine(it.config) && <div className="text-[11px] text-ink-soft mt-0.5">{configLine(it.config)}</div>}
            </div>
            <div className="text-sm text-ink whitespace-nowrap">{formatPKR(it.price * it.quantity)}</div>
          </li>
        ))}
      </ul>

      {/* Discount input (UI only — backend wiring deferred) */}
      <div className="mt-5 flex gap-2" data-testid="discount-row">
        <div className="relative flex-1">
          <Tag size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
          <input
            type="text"
            value={discountCode}
            onChange={(e) => setDiscountCode(e.target.value)}
            placeholder="Discount code"
            className="w-full h-11 pl-9 pr-3 bg-white border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-sage focus:border-sage rounded-md"
            data-testid="discount-input"
          />
        </div>
        <button
          type="button"
          onClick={() => discountCode && toast("Discount codes are coming soon")}
          className="px-4 h-11 border border-border-subtle text-xs uppercase tracking-widest text-ink-soft hover:text-ink hover:border-ink rounded-md"
          data-testid="apply-discount-btn"
        >
          Apply
        </button>
      </div>

      <div className="mt-6 pt-4 border-t border-border-subtle space-y-2 text-sm">
        <div className="flex justify-between text-ink-soft"><span>Subtotal</span><span data-testid="summary-subtotal">{formatPKR(subtotal)}</span></div>
        {discount > 0 && (
          <div className="flex justify-between text-sage-dark font-semibold" data-testid="summary-discount">
            <span>Bulk discount (5%)</span>
            <span>− {formatPKR(discount)}</span>
          </div>
        )}
        <div className="flex justify-between text-ink-soft">
          <span>Shipping</span>
          <span>{formatPKR(shippingFee)}</span>
        </div>
        <div className="flex justify-between font-display text-2xl text-ink pt-2 border-t border-ink/15">
          <span>Total <span className="text-[10px] uppercase tracking-widest text-ink-soft ml-1">PKR</span></span>
          <span data-testid="summary-total">{formatPKR(total)}</span>
        </div>
      </div>
    </>
  );
}

function EmptyCheckout() {
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <header className="border-b border-border-subtle bg-white py-5">
        <div className="max-w-6xl mx-auto px-6 flex items-center gap-3">
          <img src={LOGO_URL} alt="" className="h-10 w-10 object-contain" />
          <div>
            <div className="font-display text-xl text-ink">Bedding Lounge</div>
            <div className="label-eyebrow !text-[9px]">Checkout</div>
          </div>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center max-w-md" data-testid="checkout-empty">
          <ShoppingBag size={32} className="text-sage mx-auto mb-4" />
          <h1 className="font-display text-4xl text-ink">Your bag is empty</h1>
          <p className="text-ink-soft mt-3">Add a piece of soft luxury before checking out.</p>
          <Link to="/" className="inline-block mt-8 bg-sage hover:bg-sage-dark text-cream px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold transition-colors">
            Return to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
